import { StyleProp, TextStyle } from "react-native";

import { FeatureFlag, isReadyForPublicVariant } from "./featureFlag";
import { isPublicVariant } from "./expo";

import { toReadableText } from "@/formatting/text";
import {
  CompatibilityType,
  RecommendationCategory,
  RecommendationHighlightType,
} from "@/schemas/user/explore";

export type ProfileCardState = "normal" | "locked" | "overlay";

export const EXPLORE_CATEGORY_DISPLAY_NAMES: Partial<
  Record<RecommendationCategory, string>
> = {
  [RecommendationCategory.DATING]: "Featured",
};

export function getCategoryDisplayName(
  category: RecommendationCategory,
): string {
  return EXPLORE_CATEGORY_DISPLAY_NAMES[category] ?? toReadableText(category);
}

export const EXPLORE_CONSTANTS = {
  ASPECT_RATIO: 4 / 3,
  MAX_INTERESTS_DISPLAY_VERTICAL: 3,
  MAX_INTERESTS_DISPLAY_HORIZONTAL: 3,
  MAX_ABOUT_ME_LENGTH: 192,
  MAX_HIGHLIGHTS_LARGE_CARD: 8,
  MAX_HIGHLIGHTS_SMALL_CARD: 2,
} as const;

// used in EXPLORE_HEADER_WORDS_BY_CATEGORY
export type ExploreWord = {
  text: string;
  color: string;
};

export const EXPLORE_HEADER_WORDS_BY_CATEGORY: Record<
  RecommendationCategory,
  ExploreWord[]
> = {
  [RecommendationCategory.NEARBY]: [{ text: "Local", color: "#8B00FF" }],

  [RecommendationCategory.FRIENDS]: [
    { text: "Friend", color: "#FFD300" },
    { text: "Buddy", color: "#00FF88" },
  ],

  [RecommendationCategory.NAUGHTY]: [
    { text: "Daddy", color: "#FF006E" },
    { text: "Twink", color: "#8B00FF" },
    { text: "Bear", color: "#00D4FF" },
    { text: "Otter", color: "#00FF88" },
    { text: "Jock", color: "#FFD300" },
  ],

  [RecommendationCategory.DATING]: [{ text: "Surprise", color: "#FF006E" }],

  [RecommendationCategory.WILDCARD]: [
    { text: "Surprise", color: "#8B00FF" },
    { text: "Vibe", color: "#00D4FF" },
  ],
  // TODO: come up with words for these categories and update test
  [RecommendationCategory.ONLINE_NOW]: [{ text: "Friend", color: "#FFD300" }],
  [RecommendationCategory.NEW_USERS]: [{ text: "Friend", color: "#FFD300" }],
  [RecommendationCategory.TRENDING]: [{ text: "Friend", color: "#FFD300" }],
} as const;

/**
 * Gets explore header words for given categories with FRIENDS fallback
 * @param categories - Array of recommendation categories to get words for
 * @returns Array of word objects with text and color properties
 */
export const getExploreHeaderWords = (
  categories: RecommendationCategory[],
): ExploreWord[] => {
  // Early return for empty/undefined categories
  if (!categories?.length) {
    return EXPLORE_HEADER_WORDS_BY_CATEGORY[RecommendationCategory.FRIENDS];
  }

  // Combine words from all valid categories
  const combinedWords = categories.flatMap(
    (category) => EXPLORE_HEADER_WORDS_BY_CATEGORY[category] ?? [],
  );

  // Fallback to FRIENDS if no valid words found
  if (combinedWords.length === 0) {
    return EXPLORE_HEADER_WORDS_BY_CATEGORY[RecommendationCategory.FRIENDS];
  }

  // Remove duplicates by text, keeping first occurrence
  const uniqueWords = combinedWords.filter(
    (word, index, self) =>
      index === self.findIndex((w) => w.text === word.text),
  );

  return uniqueWords;
};

// Icon configuration for explore categories due to sizing inconsistent icon sets
export type IconConfig = {
  iconFamily: "Ionicons" | "Foundation" | "MaterialCommunityIcons" | "Entypo"; // eslint-disable-line @cspell/spellchecker
  name: string;
  sizeMultiplier?: number;
  style?: StyleProp<TextStyle>;
};

export const EXPLORE_CATEGORY_ICONS: Record<
  RecommendationCategory,
  IconConfig
> = {
  [RecommendationCategory.FRIENDS]: {
    iconFamily: "Ionicons",
    name: "people-sharp",
  },
  [RecommendationCategory.DATING]: {
    iconFamily: "Ionicons",
    name: "heart-sharp",
  },
  [RecommendationCategory.NAUGHTY]: {
    iconFamily: "MaterialCommunityIcons",
    name: "lightning-bolt",
    sizeMultiplier: 1.25,
    style: {
      marginLeft: -3,
      marginRight: -3,
      marginTop: -3,
      marginBottom: -3,
    } as TextStyle,
  },
  [RecommendationCategory.WILDCARD]: {
    iconFamily: "MaterialCommunityIcons",
    name: "bullseye-arrow",
  },
  [RecommendationCategory.NEARBY]: {
    iconFamily: "Ionicons",
    name: "location-sharp",
  },
  [RecommendationCategory.ONLINE_NOW]: {
    iconFamily: "Entypo", // eslint-disable-line @cspell/spellchecker
    name: "signal",
  },
  [RecommendationCategory.NEW_USERS]: {
    iconFamily: "Foundation",
    name: "burst-new",
    sizeMultiplier: 1.25,
    style: {
      marginLeft: -3,
      marginRight: -3,
      marginTop: -3,
      marginBottom: -3,
    } as TextStyle,
  },
  [RecommendationCategory.TRENDING]: {
    iconFamily: "MaterialCommunityIcons",
    name: "trending-up",
  },
};

// used in CompatibilityScores component
/**
 * Gets the emoji for a compatibility type
 * @param type - The compatibility type (dating/friends, forward/reverse)
 * @returns Emoji string with trailing space for the given type
 */
export function getCompatibilityTypeEmoji(type: CompatibilityType): string {
  switch (type) {
    case CompatibilityType.DATING_FORWARD:
    case CompatibilityType.DATING_REVERSE:
      return "💖 ";
    case CompatibilityType.FRIENDS_FORWARD:
    case CompatibilityType.FRIENDS_REVERSE:
      return "💛 ";
    default:
      return "🌟 ";
  }
}

/**
 * Calculate the dimensions for a grid card given container constraints.
 *
 * Computes an integer card width by subtracting spacing (including screen edge)
 * from the total `width`, dividing by `numColumns`, and applying
 * an optional `scaleFactor`. Height is derived from the provided
 * `aspectRatio` and the same `scaleFactor`.
 *
 * @param width - Total available container width
 * @param spacing - Total spacing between cards including screen edges
 * @param numColumns - Number of columns in the grid
 * @param aspectRatio - Width-to-height ratio for the card (default from EXPLORE_CONSTANTS)
 * @param scaleFactor - Multiplier to scale the final dimensions (default 1.0)
 * @returns Object containing numeric `width` and `height`
 */
export function calculateCardDimensions(
  width: number,
  spacing: number,
  numColumns: number,
  aspectRatio: number = EXPLORE_CONSTANTS.ASPECT_RATIO,
  scaleFactor = 1.0,
): { width: number; height: number } {
  const calculatedWidth = Math.floor((width - spacing) / numColumns);
  return {
    width: calculatedWidth * scaleFactor,
    height: calculatedWidth * aspectRatio * scaleFactor,
  };
}

export function getHighlightEmoji(type: RecommendationHighlightType): string {
  switch (type) {
    case RecommendationHighlightType.COMMON_INTERESTS:
    case RecommendationHighlightType.SHARED_INTERESTS:
    case RecommendationHighlightType.MANY_SHARED_INTERESTS:
      return "🎯 ";
    case RecommendationHighlightType.RECENT_ACTIVITY:
      return "🕒 ";
    case RecommendationHighlightType.SHARED_LANGUAGE:
      return "🗣️ ";
    case RecommendationHighlightType.NEARBY:
      return "📍 ";
    case RecommendationHighlightType.SIMILAR_AGE:
      return "🎂 ";
    // case RecommendationHighlightType.SAME_ETHNICITY:
    /////////////////////////
    // Body Type Preference //
    // case RecommendationHighlightType.BODY_TYPE_FIT:
    // case RecommendationHighlightType.BODY_TYPE_MUSCULAR:
    // case RecommendationHighlightType.BODY_TYPE_TONED:
    // case RecommendationHighlightType.BODY_TYPE_SLIM:
    // case RecommendationHighlightType.BODY_TYPE_AVERAGE:
    // case RecommendationHighlightType.BODY_TYPE_STOCKY:
    // case RecommendationHighlightType.BODY_TYPE_CHUBBY:
    // case RecommendationHighlightType.BODY_TYPE_LARGE:
    /////////////////////////
    // Dating Preferences: Role Preference //
    case RecommendationHighlightType.ROLE_TOP:
      return "⬆️ ";
    case RecommendationHighlightType.ROLE_BOTTOM:
      return "⬇️ ";
    case RecommendationHighlightType.ROLE_VERSATILE:
      return "↕️ ";
    case RecommendationHighlightType.ROLE_VERS_TOP:
      return "↗️ ";
    case RecommendationHighlightType.ROLE_VERS_BOTTOM:
      return "↘️ ";
    case RecommendationHighlightType.ROLE_SIDE:
      return "↔️ ";
    /////////////////////////
    // Seeking/Looking For //
    case RecommendationHighlightType.SEEKING_RELATIONSHIP:
    case RecommendationHighlightType.SEEKING_DATES:
      return "❤️ ";
    case RecommendationHighlightType.SEEKING_CASUAL:
      return "🌴 ";
    case RecommendationHighlightType.SEEKING_ROMANCE:
      return "💖 ";
    case RecommendationHighlightType.SEEKING_FRIENDS:
      return "👥 ";
    case RecommendationHighlightType.SEEKING_RIGHT_NOW:
      return "⚡ ";
    case RecommendationHighlightType.SEEKING_OPEN_RELATIONSHIP:
      return "🔓 ";
    // case RecommendationHighlightType.SEEKING_POLYAMORY:
    case RecommendationHighlightType.SEEKING_LONG_TERM:
      return "🔗 ";
    case RecommendationHighlightType.SEEKING_COFFEE_DATES:
      return "☕ ";
    default:
      return " ";
  }
}

/**
 * Check if filter should be shown for a category
 */
export function shouldShowCategoryFilter(
  category: RecommendationCategory,
): boolean {
  switch (category) {
    case RecommendationCategory.NEARBY:
      return (
        !isPublicVariant() || isReadyForPublicVariant(FeatureFlag.NEARBY_FILTER)
      );
    default:
      return false; // No filter implemented for this category
  }
}

/**
 * Check if unlocking flow should be shown for a category
 */
export function shouldShowCategoryUnlockingFlow(
  category: RecommendationCategory,
): boolean {
  switch (category) {
    case RecommendationCategory.NEARBY:
      return (
        !isPublicVariant() ||
        isReadyForPublicVariant(FeatureFlag.NEARBY_LOCKING)
      );
    case RecommendationCategory.TRENDING:
      return (
        !isPublicVariant() ||
        isReadyForPublicVariant(FeatureFlag.TRENDING_LOCKING)
      );
    default:
      return false; // No unlocking enabled for this category
  }
}
